import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrafficBadge } from "@/components/Atoms";
import { CalendarHeart, ChartLineUp, Wallet, ShieldCheck, Lightning, ArrowsClockwise, Crown, CloudArrowDown, UsersThree, Calendar } from "@phosphor-icons/react";
import DocumentosSST from "@/components/DocumentosSST";
import CronogramaBadge from "@/components/CronogramaBadge";

const COP = (n) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(n || 0);

export default function ClientPortal() {
    const { user } = useAuth();
    const [sub, setSub] = useState(null);
    const [savings, setSavings] = useState(null);
    const [inspections, setInspections] = useState([]);
    const [orders, setOrders] = useState([]);
    const [showUrgent, setShowUrgent] = useState(false);

    const load = async () => {
        const cid = user?.client_id;
        if (!cid) return;
        const [s, sv, ins, ord] = await Promise.all([
            api.get(`/clients/${cid}/subscription`),
            api.get(`/clients/${cid}/savings`),
            api.get("/inspections"),
            api.get("/orders"),
        ]);
        setSub(s.data); setSavings(sv.data); setInspections(ins.data); setOrders(ord.data);
    };
    useEffect(() => { load(); }, [user]);

    if (!sub) {
        return (
            <div className="max-w-4xl mx-auto p-8 text-center">
                <div className="overline mb-2">Sin suscripción activa</div>
                <h2 className="font-display text-3xl font-black tracking-tighter">Contacta a tu asesor comercial</h2>
                <p className="text-zinc-600 mt-2 text-sm">No tienes un plan activo en este momento.</p>
            </div>
        );
    }

    const ahorro = savings?.ahorro_neto_cop || 0;
    const ahorroPositivo = ahorro >= 0;

    // Próximas visitas técnicas (no eval inicial, no cerradas)
    const upcoming = orders
        .filter(o => o.tipo !== "evaluacion_inicial" && o.estado !== "cerrada")
        .sort((a, b) => (a.fecha_programada || "").localeCompare(b.fecha_programada || ""));

    const downloadHse = async (orderId, orderNumber) => {
        try {
            const r = await api.get(`/orders/${orderId}/hse-bundle`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `expediente_hse_${orderNumber}.zip`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { alert(e.response?.data?.detail || "Error al descargar expediente"); }
    };

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8" data-testid="client-portal">
            {/* Header */}
            <div className="mb-8 border-b border-zinc-200 pb-6">
                <div className="overline mb-2" style={{ color: "#0F766E" }}>Portal del cliente</div>
                <h1 className="font-display text-4xl font-black tracking-tighter">Bienvenido a tu panel</h1>
                <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
                    Aquí ves cuánto te ha ahorrado ATIA Construcciones al detectar a tiempo cada hallazgo, los reportes de visitas y el estado de tu suscripción.
                </p>
            </div>

            {/* Plan / Proyecto card */}
            <div className="grid lg:grid-cols-[2fr_1fr] gap-6 mb-8">
                {sub.modalidad === "proyecto" ? (
                    <ProjectCard sub={sub} user={user} onReload={load} />
                ) : (
                <div className="border border-zinc-200 p-6 bg-zinc-950 text-white grain-overlay relative" data-testid="plan-card">
                    <div className="overline mb-1" style={{ color: "rgba(94,234,212,0.8)" }}>Tu plan activo</div>
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <h2 className="font-display text-4xl font-black tracking-tighter">{sub.plan?.nombre}</h2>
                            <p className="text-xs text-zinc-400 mt-1 max-w-md">{sub.plan?.descripcion}</p>
                        </div>
                        <div className="text-right">
                            <div className="font-mono text-3xl font-bold">{sub.dias_restantes}</div>
                            <div className="text-[10px] uppercase tracking-widest text-zinc-400">días restantes</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10 text-sm">
                        <div><div className="overline mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>Frecuencia</div><div className="font-bold">Cada {sub.plan?.frecuencia_dias} días</div></div>
                        <div><div className="overline mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>Visitas realizadas</div><div className="font-bold font-mono">{savings?.visitas_realizadas ?? sub.visitas_realizadas ?? 0}</div></div>
                        <div><div className="overline mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>Valor mensual</div><div className="font-bold font-mono">{COP(sub.precio_mensual)}</div></div>
                    </div>
                    <div className="flex gap-2 mt-6">
                        <button onClick={async () => {
                            const r = await api.get(`/clients/${user.client_id}/management-report`, { responseType: "blob" });
                            const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                            const a = document.createElement("a"); a.href = url; a.download = `informe_ejecutivo_${user.client_id}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                        }} className="btn-accent text-xs flex items-center gap-2" data-testid="download-management-report">
                            📊 Descargar informe ejecutivo (con gráficos)
                        </button>
                        <button className="text-xs px-3 py-2 border border-white/30 hover:border-white/60 flex items-center gap-2" data-testid="upgrade-plan"><Crown size={14} weight="fill" /> Upgrade</button>
                        <button className="text-xs px-3 py-2 border border-white/30 hover:border-white/60 flex items-center gap-2" data-testid="renew-plan"><ArrowsClockwise size={14} /> Renovar</button>
                    </div>
                </div>
                )}

                {/* ROI summary */}
                <div className="border border-zinc-200 p-6" data-testid="roi-card">
                    <div className="overline mb-3" style={{ color: "#0F766E" }}>Tu ROI · Reporte para junta</div>
                    <div className="mb-4">
                        <div className="overline text-[10px]">Ahorro neto estimado</div>
                        <div className="font-display text-3xl font-black tracking-tighter" style={{ color: ahorroPositivo ? "#10B981" : "#EF4444" }}>{COP(ahorro)}</div>
                    </div>
                    <div className="space-y-2 text-xs border-t border-zinc-100 pt-3">
                        <div className="flex justify-between"><span className="text-zinc-600">Costos evitados estimados</span><span className="font-mono font-bold">{COP(savings?.total_evitado_cop)}</span></div>
                        <div className="flex justify-between"><span className="text-zinc-600">Inversión en plan</span><span className="font-mono">{COP(savings?.inversion_plan_cop)}</span></div>
                        <div className="flex justify-between pt-2 border-t border-zinc-100">
                            <span className="text-zinc-600">ROI</span>
                            <span className="font-mono font-bold">{savings?.roi !== null ? `${Math.round(savings.roi * 100)}%` : "—"}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Meta cero críticos */}
            <div className="border border-zinc-200 p-6 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="zero-critical-card" style={{ background: savings?.meta_cero_criticos ? "#ECFDF5" : "#FEF2F2" }}>
                <div className="flex items-center gap-4">
                    <ShieldCheck size={42} weight="duotone" style={{ color: savings?.meta_cero_criticos ? "#10B981" : "#EF4444" }} />
                    <div>
                        <div className="overline mb-1" style={{ color: savings?.meta_cero_criticos ? "#065F46" : "#991B1B" }}>Meta: cero eventos críticos</div>
                        <div className="font-display text-2xl font-bold tracking-tight">
                            {savings?.meta_cero_criticos
                                ? "¡Meta cumplida! 0 eventos críticos en tu operación."
                                : `${savings?.eventos_criticos} evento(s) crítico(s) detectado(s) — atender prioritario.`}
                        </div>
                    </div>
                </div>
                <div className="flex gap-1">
                    {Object.entries(savings?.hallazgos_por_severidad || {}).map(([sev, n]) => (
                        <div key={sev} className="px-3 py-2 text-center" style={{ background: "#fff", border: "1px solid #E4E4E7" }}>
                            <div className="font-mono text-lg font-bold">{n}</div>
                            <div className="text-[9px] uppercase text-zinc-500 tracking-wider">{sev}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Próximas visitas técnicas con expediente HSE */}
            <div className="mb-8" data-testid="upcoming-hse-section">
                <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                    <div>
                        <div className="overline mb-1" style={{ color: "#0F766E" }}>Cumplimiento SST · ISO 45001:2018</div>
                        <h3 className="font-display text-2xl font-bold tracking-tight">Próximas visitas técnicas — Expediente HSE</h3>
                        <p className="text-sm text-zinc-600 mt-1 max-w-2xl">Descarga los documentos de seguridad y salud del personal asignado para legalizar el acceso a tu sitio.</p>
                    </div>
                </div>
                {upcoming.length === 0 ? (
                    <div className="p-8 border border-dashed border-zinc-300 text-center text-zinc-500 text-sm" data-testid="no-upcoming">
                        No tienes visitas técnicas programadas en este momento.
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {upcoming.slice(0, 6).map(o => {
                            const helpers = o.helpers || [];
                            return (
                                <div key={o.order_id} className="border border-zinc-200 p-4 flex flex-col" data-testid={`upcoming-card-${o.order_id}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-mono text-xs font-bold">{o.order_number}</span>
                                        <span className="badge-sev sev-media">{o.estado}</span>
                                    </div>
                                    <div className="text-xs text-zinc-500 flex items-center gap-1 mb-1"><Calendar size={11} /> {o.fecha_programada?.slice(0, 10)}</div>
                                    <div className="font-semibold text-sm">{o.sistema_a_revisar}</div>
                                    <div className="mt-2 pt-2 border-t border-zinc-100 text-xs flex-1">
                                        <div className="overline text-[9px] mb-1" style={{ color: "#0F766E" }}>Personal asignado</div>
                                        <div className="flex items-start gap-1.5">
                                            <UsersThree size={12} className="mt-0.5 text-zinc-500" />
                                            <div>
                                                <div className="text-zinc-700">{o.technician_name || o.technician_cedula}{o.technician_especialidad && <span className="text-zinc-500"> · {o.technician_especialidad}</span>}</div>
                                                {helpers.map(h => (
                                                    <div key={h.cedula} className="text-zinc-500">+ {h.nombre} <span className="text-[10px]">(ayudante)</span></div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <button onClick={() => downloadHse(o.order_id, o.order_number)} className="btn-accent mt-3 text-xs flex items-center justify-center gap-2" data-testid={`portal-hse-zip-${o.order_id}`}>
                                        <CloudArrowDown size={14} weight="bold" /> Descargar expediente HSE
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Documentos comerciales — respaldo del alcance */}
            <ContractDocuments sub={sub} />

            {/* Urgent order */}
            <div className="mb-8 border border-zinc-200 p-6">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <div className="overline mb-1">Soporte fuera del plan</div>
                        <h3 className="font-display text-2xl font-bold tracking-tight">¿Necesitas una visita urgente?</h3>
                        <p className="text-sm text-zinc-600 mt-1 max-w-xl">Crea una orden de servicio para correctivos o casos fortuitos. Se facturará aparte de tu plan según la tarifa indicada.</p>
                    </div>
                    <button onClick={() => setShowUrgent(s => !s)} className="btn-accent flex items-center gap-2" data-testid="toggle-urgent">
                        <Lightning size={16} weight="fill" /> {showUrgent ? "Cancelar" : "Solicitar visita urgente"}
                    </button>
                </div>
                {showUrgent && <UrgentForm clientId={user?.client_id} onCreated={() => { setShowUrgent(false); load(); }} />}
            </div>

            {/* Inspections list */}
            <div className="mb-4">
                <div className="overline mb-1">Reportes de visitas</div>
                <h3 className="font-display text-2xl font-bold tracking-tight">Historial técnico</h3>
            </div>
            <div className="border border-zinc-200 overflow-x-auto">
                <table className="atia-table" data-testid="client-inspections-table">
                    <thead><tr><th>Orden</th><th>Fecha</th><th>Sistema</th><th>Técnico</th><th>Estado</th><th>Acta</th></tr></thead>
                    <tbody>
                        {inspections.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-zinc-500">Aún no se han realizado visitas.</td></tr>}
                        {inspections.map(it => (
                            <tr key={it.inspection_id} className="row-link" onClick={() => (window.location.href = `/inspections/${it.inspection_id}`)}>
                                <td className="font-mono text-xs">{it.order_number}</td>
                                <td className="font-mono text-xs">{it.fecha_ingreso?.slice(0, 10)}</td>
                                <td>{it.sistema_revisado}</td>
                                <td className="text-zinc-700 text-xs">{it.technician_name}</td>
                                <td><TrafficBadge value={it.estado_salud} /></td>
                                <td><Link to={`/inspections/${it.inspection_id}`} className="text-xs font-semibold" style={{ color: "#0F766E" }}>Ver acta →</Link></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {user?.client_id && <DocumentosSST clientId={user.client_id} canCreate={false} />}
        </div>
    );
}

function UrgentForm({ clientId, onCreated }) {
    const [form, setForm] = useState({ tipo: "correctivo", motivo: "", sistema_a_revisar: "" });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await api.post(`/clients/${clientId}/urgent-order`, form);
            setResult(r.data);
            setTimeout(() => { onCreated(); }, 2500);
        } catch (err) { alert(err.response?.data?.detail || "Error"); setLoading(false); }
    };
    if (result) {
        return (
            <div className="p-4 border border-emerald-200 bg-emerald-50 text-emerald-900" data-testid="urgent-result">
                <div className="font-bold mb-1">✓ Orden urgente creada</div>
                <div className="text-sm">N°: <span className="font-mono">{result.order_number}</span> · Tipo: {result.tipo} · Tarifa adicional: <span className="font-mono">{COP(result.tarifa_extra_cop)}</span></div>
                <div className="text-xs mt-1 text-emerald-700">Un técnico será asignado y contactado en breve.</div>
            </div>
        );
    }
    return (
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-zinc-200" data-testid="urgent-form">
            <label>
                <div className="overline mb-1.5">Tipo *</div>
                <div className="flex gap-0 border border-zinc-200">
                    {[{ v: "correctivo", l: "Correctivo · $350.000" }, { v: "fortuito", l: "Fortuito · $250.000" }].map(t => (
                        <button type="button" key={t.v} onClick={() => setForm({ ...form, tipo: t.v })} className={`flex-1 px-3 py-2 text-xs font-bold uppercase border-r last:border-r-0 border-zinc-200 ${form.tipo === t.v ? "bg-zinc-900 text-white" : "bg-white"}`}>
                            {t.l}
                        </button>
                    ))}
                </div>
            </label>
            <label>
                <div className="overline mb-1.5">Sistema afectado *</div>
                <input className="input-atia" required value={form.sistema_a_revisar} onChange={e => setForm({ ...form, sistema_a_revisar: e.target.value })} data-testid="urg-sistema" />
            </label>
            <label className="md:col-span-2">
                <div className="overline mb-1.5">Motivo / descripción *</div>
                <textarea rows={3} className="input-atia" required value={form.motivo} onChange={e => setForm({ ...form, motivo: e.target.value })} data-testid="urg-motivo" />
            </label>
            <div className="md:col-span-2 flex justify-end">
                <button type="submit" disabled={loading} className="btn-accent flex items-center gap-2" data-testid="urg-submit">
                    <Lightning size={16} weight="fill" /> {loading ? "Enviando…" : "Confirmar orden urgente"}
                </button>
            </div>
        </form>
    );
}

function ProjectCard({ sub, user, onReload }) {
    const progreso = sub.progreso_pct || 0;
    const presupuesto = sub.presupuesto_total_cop || 0;
    const ejecutado = sub.costo_ejecutado_cop || 0;
    const hitos = sub.hitos || [];
    const estadoLabels = { planeado: "Planeado", en_ejecucion: "En ejecución", cerrado: "Cerrado", cancelado: "Cancelado" };

    return (
        <div className="border border-zinc-200 p-6 bg-zinc-950 text-white grain-overlay relative" data-testid="project-card">
            <div className="flex items-center justify-between mb-2">
                <div className="overline" style={{ color: "rgba(94,234,212,0.8)" }}>Tu proyecto activo · Modalidad puntual</div>
                <div className="flex items-center gap-2">
                    {sub.cronograma && <CronogramaBadge cronograma={sub.cronograma} />}
                    <span className="text-[10px] px-2 py-1 border border-white/30 uppercase tracking-widest">{estadoLabels[sub.estado_proyecto] || sub.estado_proyecto}</span>
                </div>
            </div>
            <h2 className="font-display text-3xl font-black tracking-tighter leading-tight">{sub.nombre_proyecto || "Proyecto de mantenimiento"}</h2>
            {sub.tiene_acta_inicio && (
                <a href="#" onClick={async (e) => {
                    e.preventDefault();
                    const r = await api.get(`/subscriptions/${sub.subscription_id}/acta-inicio/pdf`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                    const a = document.createElement("a"); a.href = url; a.download = `acta_inicio_${sub.subscription_id}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                }} className="text-xs font-semibold underline inline-block mt-1" style={{ color: "rgba(94,234,212,0.9)" }} data-testid="client-download-acta-inicio">
                    Ver Acta de Inicio →
                </a>
            )}
            {sub.alcance_descripcion && <p className="text-xs text-zinc-400 mt-2 max-w-xl">{sub.alcance_descripcion}</p>}
            {sub.numero_contrato && <div className="text-[11px] font-mono mt-2 text-zinc-500">Contrato: {sub.numero_contrato}</div>}

            {/* Progreso */}
            <div className="mt-6">
                <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-zinc-400">Progreso del proyecto</span>
                    <span className="font-mono font-bold">{sub.visitas_realizadas || 0} de {sub.visitas_planeadas || 0} visitas ({progreso}%)</span>
                </div>
                <div className="h-2 bg-white/10 rounded-none overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${progreso}%`, background: progreso >= 100 ? "#10B981" : "#14B8A6" }} />
                </div>
            </div>

            {/* Ejecución financiera */}
            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10 text-sm">
                <div>
                    <div className="overline mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>Presupuesto total</div>
                    <div className="font-bold font-mono">{COP(presupuesto)}</div>
                </div>
                <div>
                    <div className="overline mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>Ejecutado</div>
                    <div className="font-bold font-mono">{COP(ejecutado)}</div>
                </div>
                <div>
                    <div className="overline mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>Días restantes</div>
                    <div className="font-bold font-mono">{sub.dias_restantes}</div>
                </div>
            </div>

            {/* Hitos */}
            {hitos.length > 0 && (
                <div className="mt-6 pt-6 border-t border-white/10">
                    <div className="overline mb-3" style={{ color: "rgba(255,255,255,0.5)" }}>Cronograma de hitos</div>
                    <div className="space-y-2">
                        {hitos.map((h, i) => {
                            const badge = { programado: "border-white/20 text-zinc-400", facturado: "border-amber-400 text-amber-300", pagado: "border-emerald-400 text-emerald-300" }[h.estado] || "border-white/20";
                            return (
                                <div key={i} className="flex items-center justify-between text-xs" data-testid={`hito-${i}`}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-2 h-2 rounded-full" style={{ background: h.estado === "pagado" ? "#10B981" : h.estado === "facturado" ? "#F59E0B" : "#71717A" }} />
                                        <span className="text-zinc-200">{h.nombre}</span>
                                        <span className="font-mono text-zinc-400">{h.porcentaje}%</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="font-mono font-bold">{COP(h.monto_cop)}</span>
                                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${badge}`}>{h.estado}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="flex gap-2 mt-6 flex-wrap">
                <button onClick={async () => {
                    const r = await api.get(`/clients/${user.client_id}/management-report`, { responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                    const a = document.createElement("a"); a.href = url; a.download = `informe_ejecutivo_${user.client_id}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                }} className="btn-accent text-xs flex items-center gap-2" data-testid="download-management-report">
                    📊 Informe de avance
                </button>
                {progreso >= 100 && (
                    <button className="text-xs px-3 py-2 border border-emerald-400/60 hover:border-emerald-400 text-emerald-300 flex items-center gap-2" data-testid="download-certificate">
                        ✅ Certificado de cierre
                    </button>
                )}
            </div>
        </div>
    );
}



function ContractDocuments({ sub }) {
    const download = async (kind, defaultName) => {
        try {
            const r = await api.get(`/subscriptions/${sub.subscription_id}/${kind}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = defaultName; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { alert(e.response?.data?.detail || "Documento no disponible aún"); }
    };

    const items = [
        { kind: "diagnostic", label: "Diagnóstico técnico", desc: "Análisis inicial que respalda el alcance ofrecido", available: sub.diagnostico_disponible, filename: `diagnostico_${sub.subscription_id}.pdf` },
        { kind: "quote",      label: "Cotización aprobada", desc: "Propuesta económica presentada y aprobada",       available: sub.cotizacion_disponible,  filename: `cotizacion_${sub.subscription_id}.pdf` },
        { kind: "contract",   label: "Contrato firmado",    desc: "Contrato firmado por representante legal",         available: sub.contrato_disponible,    filename: `contrato_${sub.subscription_id}.pdf` },
    ];

    return (
        <div className="mb-8 border border-zinc-200 p-6" data-testid="contract-documents">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                    <div className="overline mb-1" style={{ color: "#0F766E" }}>Documentos de tu contrato</div>
                    <h3 className="font-display text-2xl font-bold tracking-tight">Respaldo del alcance acordado</h3>
                    <p className="text-sm text-zinc-600 mt-1 max-w-2xl">Descarga los soportes formales de tu contrato. El alcance que ves en tu panel corresponde a estos documentos.</p>
                </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
                {items.map(it => (
                    <div key={it.kind} className={`border p-4 ${it.available ? "border-zinc-300 bg-white" : "border-dashed border-zinc-300 bg-zinc-50"}`} data-testid={`contract-doc-${it.kind}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-semibold text-sm">{it.label}</div>
                            <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border ${it.available ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-800"}`}>
                                {it.available ? "DISPONIBLE" : "PENDIENTE"}
                            </span>
                        </div>
                        <div className="text-[11px] text-zinc-500 mb-3">{it.desc}</div>
                        {it.available ? (
                            <button onClick={() => download(it.kind, it.filename)} className="btn-accent text-xs w-full flex items-center justify-center gap-2" data-testid={`download-${it.kind}`}>
                                <CloudArrowDown size={14} weight="bold" /> Descargar PDF
                            </button>
                        ) : (
                            <div className="text-[11px] text-zinc-400 italic">Tu asesor comercial aún no ha cargado este documento.</div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
